import React, { createContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { Close } from '@mui/icons-material';
import { checkPermission } from '../helpers/permissions';

export const SocketContext = createContext({
    socket: null,
    isConnected: false,
    connectSocket: () => { },
    disconnectSocket: () => { },
});

/* -------------------- Notification route exclusions -------------------- */

const NOTIF_EXCLUDED_PATHS = [
    '/login',
    '/login-user',
    '/login-checklist-user',
    '/login-feedback-user',
    '/create-account',
    '/reset-password',
    '/forgot-password',
    '/auth/password',
    '/submit-feedback',
    '/qr',
    '/venue',
    '/sso',
    '/privacy-policy',
    '/register',
];

const isNotificationAllowedOnThisPage = () => {

    if (typeof window === 'undefined') return true;
    const path = window.location.pathname || '/';
    return !NOTIF_EXCLUDED_PATHS.some(p => path === p || path.startsWith(`${p}/`));
};

/* -------------------- Toast UI -------------------- */

// const TopLevelToasts = ({ companyColor = '#6366f1' }) => {
//     const [toasts, setToasts] = useState([]);

//     useEffect(() => {
//         const onNewMessage = (e) => {
//             const data = e?.detail;
//             if (!data) return;

//             const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
//             const title = data?.from_name || data?.sender_name || 'New message';
//             const body = data?.message_body || data?.message || data?.text || 'You have a new message';

//             setToasts(prev => [{ id, title, body }, ...prev].slice(0, 6));

//             setTimeout(() => {
//                 setToasts(prev => prev.filter(t => t.id !== id));
//             }, 5000);
//         };

//         window.addEventListener('app:new_message', onNewMessage);
//         return () => window.removeEventListener('app:new_message', onNewMessage);
//     }, []);

//     return (
//         <div className="sh-toast-container">
//             {toasts.map(t => (
//                 <div key={t.id} className="sh-toast" onClick={() => (window.location.href = '/chat-concierge')}>
//                     <button
//                         className="sh-toast-close"
//                         onClick={(e) => {
//                             e.stopPropagation();
//                             setToasts(prev => prev.filter(x => x.id !== t.id));
//                         }}
//                     >
//                         <Close />
//                     </button>

//                     <div className="sh-toast-avatar" style={{ background: companyColor, color: '#fff' }}>
//                         {t.title.charAt(0).toUpperCase()}
//                     </div>

//                     <div className="sh-toast-body">
//                         <div className="sh-toast-title">{t.title}</div>
//                         <div className="sh-toast-text">{t.body}</div>
//                     </div>
//                 </div>
//             ))}
//         </div>
//     );
// };

/* -------------------- Notification permission banner -------------------- */

const GlobalNotificationBanner = ({ companyColor, isLoggedIn }) => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!isLoggedIn) return setShow(false);
        if (!('Notification' in window)) return;
        setShow(Notification.permission === 'default');
    }, [isLoggedIn]);

    const enableNotifications = async () => {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                window.dispatchEvent(new Event('app:fcm:retry'));
            }
        } finally {
            setShow(false);
        }
    };

    if (!show) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 20,
                right: '50%',
                transform: 'translateX(50%)',
                zIndex: 2000,
                background: '#ffffff',
                boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                borderRadius: 10,
                padding: '12px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                border: '1px solid #e5e7eb',
            }}
        >
            {/* Close button */}
            <button
                onClick={() => setShow(false)}
                style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 18,
                    color: '#9ca3af',
                }}
                aria-label="Close notification banner"
            >
                ×
            </button>

            <div style={{ fontSize: 14, fontWeight: 600 }}>
                Enable Desktop Notifications
            </div>

            <div style={{ fontSize: 13, color: '#6b7280' }}>
                Turn on notifications to receive new message alerts.
            </div>

            <button
                onClick={enableNotifications}
                style={{
                    background: companyColor,
                    color: '#fff',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    alignSelf: 'flex-end',
                }}
            >
                Enable
            </button>
        </div>
    );
};



/* ==================== SOCKET PROVIDER ==================== */

const SocketProvider = ({ children }) => {
    const socketRef = useRef(null);
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));

    /* 🔊 Notification Sound */
    const notificationAudioRef = useRef(null);
    const audioUnlockedRef = useRef(false);

    const unlockAudio = () => {
        if (audioUnlockedRef.current) return;

        const audio = notificationAudioRef.current;
        if (!audio) return;

        audio.muted = true;

        audio.play()
            .then(() => {
                audio.pause();
                audio.currentTime = 0;
                audio.muted = false;
                audioUnlockedRef.current = true;
            })
            .catch(() => {
                // Safari blocks until user taps again
            });
    };

    useEffect(() => {
        const handler = () => unlockAudio();

        window.addEventListener('click', handler, { once: true });
        window.addEventListener('touchstart', handler, { once: true }); // Safari iOS
        window.addEventListener('keydown', handler, { once: true });

        return () => {
            window.removeEventListener('click', handler);
            window.removeEventListener('touchstart', handler);
            window.removeEventListener('keydown', handler);
        };
    }, []);

    const playSound = () => {
        const audio = notificationAudioRef.current;

        // 🔒 Safari guard
        if (!audio || !audioUnlockedRef.current) return;

        try {
            audio.currentTime = 0;
            audio.play();
        } catch {
            // Safari silently ignores failures
        }
    };

    const handleNewMessageGlobal = (data) => {
        playSound();
        window.dispatchEvent(new CustomEvent('app:new_message', { detail: data }));
    };

    const connectSocket = ({ token } = {}) => {
        if (socketRef.current?.connected) return socketRef.current;

        const serverUrl = process.env.REACT_APP_SOCKET_URL;
        const authToken = token || localStorage.getItem('token') || '';

        // Local dev: if no socket URL configured, skip connection entirely
        // rather than silently falling back to a staging server.
        if (!serverUrl) {
            return null;
        }

        socketRef.current = io(serverUrl, {
            path: '/socket.io/',
            transports: ['websocket', 'polling'],
            auth: { token: authToken },
            reconnection: true,
        });

        socketRef.current.on('connect', () => {
            setSocket(socketRef.current);
            setIsConnected(true);
        });

        socketRef.current.on('disconnect', () => setIsConnected(false));
        socketRef.current.on('connect_error', () => setIsConnected(false));
        socketRef.current.on('new_message', handleNewMessageGlobal);

        return socketRef.current;
    };

    const disconnectSocket = () => {
        socketRef.current?.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) connectSocket({ token });

        const onLogin = (e) => {
            setIsLoggedIn(true);
            connectSocket({ token: e?.detail?.token });
        };

        const onLogout = () => {
            setIsLoggedIn(false);
            disconnectSocket();
        };

        window.addEventListener('app:user:login', onLogin);
        window.addEventListener('app:user:logout', onLogout);

        return () => {
            window.removeEventListener('app:user:login', onLogin);
            window.removeEventListener('app:user:logout', onLogout);
            disconnectSocket();
        };
    }, []);

    return (
        <SocketContext.Provider value={{ socket, isConnected, connectSocket, disconnectSocket }}>
            {children}

            {checkPermission('manage_texting') &&
                isLoggedIn &&
                isNotificationAllowedOnThisPage() && (
                    <GlobalNotificationBanner companyColor="#6366f1" isLoggedIn={isLoggedIn} />
                )}

            {/* <TopLevelToasts companyColor="#6366f1" /> */}

            <audio ref={notificationAudioRef} src="/sounds/message.wav" preload="auto" />
        </SocketContext.Provider>
    );
};

export default SocketProvider;
